import React, { useEffect, useState } from 'react';
import { feedbackApi } from '../api/feedbackApi';
import appConfig from '../config/appConfig';
import { Star, Quote } from 'lucide-react';

/**
 * Social-proof strip: aggregate rating + approved/featured testimonials.
 * Shows ONLY where a super admin published it (feedback config `publish`):
 *   'off'       → never renders (default)
 *   'dashboard' → only on '/'
 *   'help'      → only on '/help'
 * Never rendered on the login page and never auto-published.
 *
 * Props: route (current pathname), or forcePreview (admin preview — ignores
 * the publish gate but still requires approved testimonials to exist).
 */
export default function SocialProof({ route, forcePreview = false, refreshKey = 0 }) {
  const [cfg, setCfg] = useState(() => appConfig.getFeedbackConfig());
  const [data, setData] = useState(null);

  useEffect(() => appConfig.subscribe(() => setCfg(appConfig.getFeedbackConfig())), []);

  const placeMatches = forcePreview
    || (cfg.publish === 'dashboard' && route === '/')
    || (cfg.publish === 'help' && route === '/help');

  useEffect(() => {
    if (!placeMatches) return; // render returns null anyway; keep any prior data harmless
    let done = false;
    feedbackApi.publicSummary().then((res) => { if (!done) setData(res?.data?.Data || null); }).catch(() => {});
    return () => { done = true; };
  }, [placeMatches, refreshKey]);

  if (!placeMatches) return null;
  const testimonials = data?.testimonials || [];
  // Nothing approved yet → don't show an empty/awkward strip (unless previewing).
  if (!testimonials.length && !forcePreview) return null;

  return (
    <div style={{ background: 'linear-gradient(135deg, #0F766E, #134E4A)', borderRadius: 16, padding: '20px 24px', marginBottom: 24, color: '#fff', boxShadow: '0 8px 30px rgba(13,148,136,0.18)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: testimonials.length ? 14 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} size={18} strokeWidth={2} color="#FCD34D" fill={Math.round(data?.average || 0) >= n ? '#FCD34D' : 'none'} />
          ))}
          <span style={{ fontSize: 20, fontWeight: 800, marginLeft: 4 }}>{data?.average ? data.average.toFixed(1) : '—'}</span>
        </div>
        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>
          {data?.count ? `from ${data.count} provider${data.count === 1 ? '' : 's'}` : 'provider satisfaction'}
        </span>
      </div>
      {testimonials.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
                  {testimonials.slice(0, testimonials.length).map((t) => (
            <div key={t.id} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '12px 14px' }}>
              <Quote size={16} color="rgba(255,255,255,0.5)" />
              <div style={{ fontSize: 13.5, lineHeight: 1.5, margin: '6px 0 8px' }}>"{t.testimonial}"</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{t.name || 'A provider'}{t.org ? ` · ${t.org}` : ''}</div>
            </div>
          ))}
        </div>
      )}
      {forcePreview && !testimonials.length && (
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginTop: 10 }}>No approved testimonials yet — approve/feature some below to populate this.</div>
      )}
    </div>
  );
}
